import React, { useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, Search } from 'lucide-react'

const SortableTable = ({ 
  data, 
  columns, 
  searchable = true, 
  searchPlaceholder = "Search...",
  className = "",
  defaultSortKey = null,
  defaultSortDirection = 'asc',
  pageSize = 25,
  showPagination = true
}) => {
  const [sortConfig, setSortConfig] = useState({ key: defaultSortKey, direction: defaultSortDirection })
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const handleSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const sortedAndFilteredData = useMemo(() => {
    let filteredData = data

    // Apply search filter
    if (searchTerm) {
      filteredData = data.filter(item =>
        columns.some(column => {
          const accessor = column.accessor
          const value = typeof accessor === 'function'
            ? accessor(item)
            : typeof accessor === 'string'
              ? item[accessor]
              : item[column.key]
          return value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        })
      )
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredData = [...filteredData].sort((a, b) => {
        const column = columns.find(col => col.key === sortConfig.key)
        const accessor = column?.accessor
        const aValue = typeof accessor === 'function'
          ? accessor(a)
          : typeof accessor === 'string'
            ? a[accessor]
            : a[sortConfig.key]
        const bValue = typeof accessor === 'function'
          ? accessor(b)
          : typeof accessor === 'string'
            ? b[accessor]
            : b[sortConfig.key]

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1
        }
        return 0
      })
    }

    return filteredData
  }, [data, columns, sortConfig, searchTerm])

  const totalPages = useMemo(() => {
    return pageSize > 0 ? Math.ceil(sortedAndFilteredData.length / pageSize) : 1
  }, [sortedAndFilteredData.length, pageSize])

  const pagedData = useMemo(() => {
    if (!showPagination || pageSize <= 0) return sortedAndFilteredData
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize
    return sortedAndFilteredData.slice(start, end)
  }, [sortedAndFilteredData, currentPage, pageSize, showPagination])

  // Reset to first page when sorting or search changes
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, sortConfig.key, sortConfig.direction])

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="w-4 h-4 text-gray-300" />
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-gray-600" /> : 
      <ChevronDown className="w-4 h-4 text-gray-600" />
  }

  return (
    <div className={className}>
      {searchable && (
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="table">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.sortable !== false ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className="flex items-center justify-between">
                    <span>{column.header}</span>
                    {column.sortable !== false && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {pagedData.map((item, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.render ? column.render(item) : (
                      typeof column.accessor === 'function'
                        ? column.accessor(item)
                        : typeof column.accessor === 'string'
                          ? item[column.accessor]
                          : item[column.key]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedAndFilteredData.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No data found</p>
        </div>
      )}

      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="space-x-2">
            <button
              className="btn-secondary"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <button
              className="btn-secondary"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SortableTable